import { ChevronDown, Search, SlidersHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Skeleton } from '~/components/ui/skeleton';
import { Switch } from '~/components/ui/switch';
import type {
  ProductAppliedFilters,
  ProductFilterDescriptor,
} from '~/features/explorer/types/products-catalog.types';
import { cn } from '~/utils/cn';

/** Facet types the sidebar renders as list/boolean controls. Other shapes
 *  (range / date_range / free text) are skipped — the passthrough filter
 *  envelope only stays correct for these. */
const LIST_TYPES = new Set(['multi_select', 'text', 'text_contains']);
/** Auto-enable an in-facet search box once the option list grows past this. */
const OPTION_SEARCH_THRESHOLD = 8;

interface ProductFacetSidebarProps {
  descriptors: ProductFilterDescriptor[];
  applied: ProductAppliedFilters;
  onChange: (next: ProductAppliedFilters) => void;
  isLoading?: boolean;
}

/** Removes a key from the applied map without a dynamic `delete`. */
function omitKey(applied: ProductAppliedFilters, key: string): ProductAppliedFilters {
  return Object.fromEntries(Object.entries(applied).filter(([k]) => k !== key));
}

/** Reads the applied string[] for a list facet (tolerant of a lone string). */
function selectedList(applied: ProductAppliedFilters, key: string): string[] {
  const v = applied[key];
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  if (typeof v === 'string' && v) return [v];
  return [];
}

/** Total number of active facet selections — drives the "Clear all" affordance. */
function countActive(applied: ProductAppliedFilters): number {
  return Object.values(applied).reduce((sum: number, v) => {
    if (Array.isArray(v)) return sum + v.length;
    if (v === true) return sum + 1;
    if (typeof v === 'string' && v) return sum + 1;
    return sum;
  }, 0);
}

/** A single collapsible multi-select facet with an optional option-search box. */
function ListFacet({
  descriptor,
  selected,
  onToggle,
}: {
  descriptor: ProductFilterDescriptor;
  selected: string[];
  onToggle: (option: string) => void;
}) {
  const { t } = useTranslation('explorer');
  const [open, setOpen] = useState(true);
  const [query, setQuery] = useState('');
  const showSearch = (descriptor.options?.length ?? 0) > OPTION_SEARCH_THRESHOLD;

  const filtered = useMemo(() => {
    const opts = descriptor.options ?? [];
    if (!query.trim()) return opts;
    const q = query.toLowerCase();
    return opts.filter((o) => o.toLowerCase().includes(q));
  }, [descriptor.options, query]);

  return (
    <div className="border-border/60 border-b py-3">
      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        className="flex h-auto w-full cursor-pointer items-center justify-between gap-2 p-0 text-left hover:bg-transparent"
      >
        <span className="text-foreground text-sm font-semibold">{descriptor.label}</span>
        <span className="flex items-center gap-1.5">
          {selected.length > 0 && (
            <span className="text-primary text-xs font-semibold tabular-nums">
              {selected.length}
            </span>
          )}
          <ChevronDown
            className={cn(
              'text-muted-foreground h-4 w-4 shrink-0 transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </span>
      </Button>

      {open && (
        <div className="mt-2.5 space-y-2">
          {showSearch && (
            <div className="relative">
              <Search
                className="text-muted-foreground absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                placeholder={t('products.facetSearch', { label: descriptor.label.toLowerCase() })}
                aria-label={t('products.facetSearch', { label: descriptor.label.toLowerCase() })}
                className="bg-background h-8 rounded-lg pl-8 text-sm shadow-none"
              />
            </div>
          )}
          <div className="max-h-[220px] space-y-0.5 overflow-y-auto pr-1">
            {filtered.map((option) => {
              const id = `facet-${descriptor.key}-${option}`;
              return (
                <label
                  key={option}
                  htmlFor={id}
                  className="hover:bg-muted/50 flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1.5"
                >
                  <Checkbox
                    id={id}
                    checked={selected.includes(option)}
                    onCheckedChange={() => {
                      onToggle(option);
                    }}
                  />
                  <span className="text-foreground truncate text-sm capitalize">{option}</span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-muted-foreground px-1.5 py-2 text-xs">
                {t('products.facetNoOptions')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** A single boolean facet rendered as a labelled toggle. */
function BooleanFacet({
  descriptor,
  checked,
  onToggle,
}: {
  descriptor: ProductFilterDescriptor;
  checked: boolean;
  onToggle: (next: boolean) => void;
}) {
  const id = `facet-${descriptor.key}`;
  return (
    <div className="border-border/60 flex items-center justify-between gap-2 border-b py-3.5">
      <Label htmlFor={id} className="text-foreground cursor-pointer text-sm font-semibold">
        {descriptor.label}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={onToggle} className="cursor-pointer" />
    </div>
  );
}

/**
 * Left-hand facet sidebar for the products browse view. Renders the dynamic
 * filter descriptors (list + boolean facets); selections commit straight into
 * the passthrough filter envelope. Per-option counts are intentionally omitted —
 * the filters endpoint does not return them.
 */
export function ProductFacetSidebar({
  descriptors,
  applied,
  onChange,
  isLoading = false,
}: ProductFacetSidebarProps) {
  const { t } = useTranslation('explorer');

  // Only list facets that actually carry options are renderable as checkbox
  // lists; free-text facets (options: null) have no usable UI here, so skip them.
  const facets = useMemo(
    () =>
      descriptors.filter(
        (d) => (LIST_TYPES.has(d.type) && (d.options?.length ?? 0) > 0) || d.type === 'boolean',
      ),
    [descriptors],
  );
  const activeCount = countActive(applied);

  function toggleOption(key: string, option: string) {
    const current = selectedList(applied, key);
    const nextList = current.includes(option)
      ? current.filter((o) => o !== option)
      : [...current, option];
    onChange(nextList.length === 0 ? omitKey(applied, key) : { ...applied, [key]: nextList });
  }

  function toggleBoolean(key: string, value: boolean) {
    onChange(value ? { ...applied, [key]: true } : omitKey(applied, key));
  }

  return (
    <aside className="flex w-full flex-col">
      <div className="border-border/60 mb-2 flex items-center justify-between gap-2 border-b pb-3">
        <span className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          {t('products.filters')}
        </span>
        {activeCount > 0 && (
          <Button
            type="button"
            variant="link"
            onClick={() => {
              onChange({});
            }}
            className="text-primary h-auto cursor-pointer p-0 text-xs font-medium"
          >
            {t('products.clearAll')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-4 pt-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-4/5" />
            </div>
          ))}
        </div>
      ) : facets.length === 0 ? (
        <p className="text-muted-foreground pt-3 text-xs">{t('products.noFiltersAvailable')}</p>
      ) : (
        <div className="flex flex-col">
          {facets.map((descriptor) =>
            descriptor.type === 'boolean' ? (
              <BooleanFacet
                key={descriptor.key}
                descriptor={descriptor}
                checked={applied[descriptor.key] === true}
                onToggle={(next) => {
                  toggleBoolean(descriptor.key, next);
                }}
              />
            ) : (
              <ListFacet
                key={descriptor.key}
                descriptor={descriptor}
                selected={selectedList(applied, descriptor.key)}
                onToggle={(option) => {
                  toggleOption(descriptor.key, option);
                }}
              />
            ),
          )}
        </div>
      )}
    </aside>
  );
}
